/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Dropdown, InlineNotification, Tile } from 'carbon-components-react';
import { OrderedMap } from 'immutable';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMount } from 'react-use';

import { useFormHelpers } from '../../../../hooks/useFormHelpers';
import { FIELDS } from '../../schema';
import { ServerMessages as messages } from './messages';
import { ServerVariables } from './serverVariables';

import './servers.scss';

interface ServersProps {
  servers: OrderedMap<string, any>[];

  serverUrl: string;
  setServerUrl: (value: any) => void;

  serverVariables: Record<string, any>;

  setServerVariableValue: (value: any) => void;
  getEffectiveServerValue: (url: string) => any;
  getServerDefaultVariableValue: (value: any) => any;
  setServerDefaultVariableValue: (value: any) => void;

  readOnly: boolean;
}

export default function Servers({
  readOnly,
  servers,
  setServerVariableValue,
  getEffectiveServerValue,
  getServerDefaultVariableValue,
  setServerDefaultVariableValue,
  setServerUrl,
  serverUrl,
  serverVariables
}: ServersProps) {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { isValid } = useFormHelpers(formMethods);

  // Current server definition item.
  const currentServer =
    servers.find(s => s.get('url') === serverUrl) ?? OrderedMap();

  // Variable definitions of the current server definition.
  const currentVariableDefinitions =
    currentServer.get('variables') ?? OrderedMap();

  /**
   * Set server URL in the draft integration settings.
   */
  const setSelectedServerUrl = useCallback(
    (serverUrl: string) =>
      setServerUrl({
        field: FIELDS.SERVER_URL,
        value: serverUrl
      }),
    [setServerUrl]
  );

  /**
   * Set generated server URL in the draft integration settings.
   */
  const setGeneratedServerUrl = useCallback(
    (serverUrl: string) =>
      setServerUrl({
        field: FIELDS.GENERATED_SERVER_URL,
        value: getEffectiveServerValue(serverUrl)
      }),
    [getEffectiveServerValue, setServerUrl]
  );

  /**
   * Wrapper method to set the server variable value in both Watson
   * Assistant code and in the Swagger UI code.
   */
  const updateServerVariableValue = useCallback(
    (url: string, key: string, value: string) => {
      // This lets Swagger UI know about the new value.
      setServerVariableValue({
        server: serverUrl,
        key,
        val: _.isEmpty(value) ? `{${key}}` : value
      });

      // And this lets Watson Assistant know about the new value.
      setServerDefaultVariableValue({ variableName: key, value });
    },
    [serverUrl, setServerDefaultVariableValue, setServerVariableValue]
  );

  /**
   * Wrapper method to set the server URL and its corresponding
   * server variables as both the plain server URL and the generated
   * server URL.
   */
  const updateServerAndVariableFields = useCallback(
    (serverUrl: string) => {
      setSelectedServerUrl(serverUrl);

      currentVariableDefinitions.map(
        (val: OrderedMap<string, any>, key: string) => {
          const currentValue =
            (servers.find(s => s.get('url') === serverUrl) ?? OrderedMap())
              .size !== 0
              ? serverVariables[key]?.value
              : undefined;

          // only set the default value if the user hasn't set one yet
          updateServerVariableValue(
            serverUrl,
            key,
            currentValue ?? val.get('default') ?? null
          );
        }
      );

      setGeneratedServerUrl(serverUrl);
    },
    [
      currentVariableDefinitions,
      servers,
      serverVariables,
      setGeneratedServerUrl,
      setSelectedServerUrl,
      updateServerVariableValue
    ]
  );

  /**
   * When the component first loads, we need to get the serverUrl and
   * make sure that any server variables it has are defaulted
   * properly.
   */
  useMount(() => {
    updateServerAndVariableFields(serverUrl);
  });

  /**
   * Callback method triggers when the user changes server from the
   * dropdown.
   */
  const onServerChange = useCallback(
    selectedItem => updateServerAndVariableFields(selectedItem),
    [updateServerAndVariableFields]
  );

  /**
   * Callback method triggers when the user changes a server variable
   * value.
   */
  const onServerVariableValueChange = (
    variableName: string,
    newVariableValue: string
  ) => {
    // Update the variable in Swagger UI and WA.
    updateServerVariableValue(serverUrl, variableName, newVariableValue);
    // We calculate the generated server url based on the new variable.
    setGeneratedServerUrl(serverUrl);
  };

  // If there are no server variables, we can just hide setting
  // server variables in the UI.
  const showVariablesUI = currentVariableDefinitions.size !== 0;

  // Create an array of server items for the dropdown to select.
  const serversDropdownItems = Array.from(servers).map(server =>
    server.get('url')
  );

  // Find which server URL in the dropdown is currently selected.
  const selectedDropdownItem =
    serversDropdownItems.find(server => server === serverUrl) ??
    serversDropdownItems[0];

  return (
    <>
      {!showVariablesUI && !isValid(FIELDS.GENERATED_SERVER_URL) && (
        <InlineNotification
          className="CatalogOpenApiServerReviewComputedUrl"
          kind="error"
          lowContrast
          title={t(messages.noVariablesInvalidUrl)}
          subtitle={getEffectiveServerValue(serverUrl)}
          hideCloseButton
        />
      )}
      <div style={{ width: '512px' }}>
        <Dropdown
          label={t(messages.servers).toLowerCase()}
          id="servers"
          titleText={t(messages.servers)}
          items={serversDropdownItems}
          onChange={value => {
            onServerChange(value.selectedItem);
          }}
          selectedItem={selectedDropdownItem}
        />
      </div>
      {showVariablesUI ? (
        <>
          <InlineNotification
            className="CatalogOpenApiServerReviewComputedUrl"
            kind={isValid(FIELDS.GENERATED_SERVER_URL) ? 'info' : 'error'}
            lowContrast
            title={
              isValid(FIELDS.GENERATED_SERVER_URL)
                ? t(messages.validUrl)
                : t(messages.invalidUrl)
            }
            subtitle={getEffectiveServerValue(serverUrl)}
            hideCloseButton
          />
          <Tile className="CatalogOpenApiServerReview">
            <h3 className="CatalogOpenApiServerReview__title">
              <FormattedMessage {...messages.serverVariables} />
            </h3>
            <div className="CatalogOpenApiServerReview__description">
              <FormattedMessage {...messages.serverVariablesDescription} />
            </div>
            {currentVariableDefinitions.entrySeq().map(([name, val], index) => (
              <ServerVariables
                readOnly={readOnly}
                key={name}
                name={name}
                val={val}
                index={index}
                serverIndex={servers?.findIndex(
                  server => server.get('url') === serverUrl
                )}
                onChange={onServerVariableValueChange}
                getServerVariable={getServerDefaultVariableValue}
              />
            ))}
          </Tile>
        </>
      ) : null}
    </>
  );
}
